import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, setLTToken, getLTToken } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BookOpen, Headphones, GripVertical, ChevronLeft, Music, Video, ChevronRight, Lock, Search, Languages, Building2, Megaphone, X, PlayCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const FOLDER_META = {
  audios: { Icon: Music, accent: "bg-[#E30613]" },
  videos: { Icon: Video, accent: "bg-black" },
  listening_test: { Icon: Lock, accent: "bg-amber-600" },
};

const LANGUAGE_META = {
  english: { title: "English", subtitle: "Cursos de Inglês", accent: "bg-[#1d4ed8]" },
  espanol: { title: "Español", subtitle: "Cursos de Espanhol", accent: "bg-[#d97706]" },
};

const DEFAULT_LABELS = {
  english: { audios: "Áudios", videos: "Vídeos", listening_test: "Listening Test" },
  espanol: { audios: "Áudios", videos: "Vídeos", listening_test: "Prueba - Escucha" },
};

export default function StudentDashboard() {
  const { user, setUnit } = useAuth();
  const isAdmin = user?.role === "admin";
  const needsUnit = !isAdmin && !!user && !user.unit;

  const [allBooks, setAllBooks] = useState([]);
  const [ltBooks, setLtBooks] = useState([]);
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [loading, setLoading] = useState(true);

  const [language, setLanguage] = useState(null); // 'english' | 'espanol' | null
  const [category, setCategory] = useState(null); // 'audios' | 'videos' | 'listening_test' | null

  const [askPwd, setAskPwd] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [pwd, setPwd] = useState("");

  const [unitPick, setUnitPick] = useState("");
  const [savingUnit, setSavingUnit] = useState(false);

  const [announcements, setAnnouncements] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [dismissedAnns, setDismissedAnns] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("fisk_dismissed_ann") || "[]")); }
    catch { return new Set(); }
  });

  const [search, setSearch] = useState("");
  const dragIdx = useRef(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [dragging, setDragging] = useState(false);

  const fetchLabels = async () => {
    try {
      const { data } = await api.get("/settings/folder-labels");
      setLabels({
        english: { ...DEFAULT_LABELS.english, ...(data.english || {}) },
        espanol: { ...DEFAULT_LABELS.espanol, ...(data.espanol || {}) },
      });
    } catch (_) {}
  };

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements");
      setAnnouncements(data);
    } catch (_) {}
  };

  const fetchProgress = async () => {
    if (isAdmin) return;
    try {
      const { data } = await api.get("/progress");
      setProgressMap(data || {});
    } catch (_) {}
  };

  const dismissAnn = (id) => {
    setDismissedAnns((prev) => {
      const next = new Set(prev); next.add(id);
      try { sessionStorage.setItem("fisk_dismissed_ann", JSON.stringify(Array.from(next))); } catch (_) {}
      return next;
    });
  };

  const fetchBooks = async () => {
    try {
      const { data } = await api.get("/books");
      setAllBooks(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fetchLt = async () => {
    if (isAdmin) return;
    if (getLTToken()) {
      try {
        const { data } = await api.get("/listening-test/books");
        setLtBooks(data);
      } catch (_) { setLTToken(""); }
    }
  };

  // Bloqueia imediatamente ao sair da pasta protegida
  const lockLT = () => {
    if (isAdmin) return;
    if (getLTToken()) setLTToken("");
    setLtBooks([]);
  };

  // Ao trocar de categoria (voltar), se saímos da listening_test → bloqueia
  const prevCat = useRef(null);
  useEffect(() => {
    if (prevCat.current === "listening_test" && category !== "listening_test") {
      lockLT();
    }
    prevCat.current = category;
    // eslint-disable-next-line
  }, [category]);

  // Ao voltar para a tela de idiomas, também bloqueia
  useEffect(() => {
    if (language === null) lockLT();
    // eslint-disable-next-line
  }, [language]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchLabels(), fetchBooks(), fetchLt(), fetchAnnouncements(), fetchProgress()]);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const counts = useMemo(() => {
    const get = (lang, cat) => {
      if (cat === "listening_test" && !isAdmin) {
        return ltBooks.filter((b) => b.language === lang).length;
      }
      return allBooks.filter((b) => b.language === lang && b.category === cat).length;
    };
    return {
      english: {
        audios: get("english", "audios"),
        videos: get("english", "videos"),
        listening_test: get("english", "listening_test"),
      },
      espanol: {
        audios: get("espanol", "audios"),
        videos: get("espanol", "videos"),
        listening_test: get("espanol", "listening_test"),
      },
    };
  }, [allBooks, ltBooks, isAdmin]);

  const visibleBooks = useMemo(() => {
    if (!language || !category) return [];
    let arr;
    if (category === "listening_test" && !isAdmin) {
      arr = ltBooks.filter((b) => b.language === language);
    } else {
      arr = allBooks.filter((b) => b.language === language && b.category === category);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((b) => (b.title || "").toLowerCase().includes(q) || (b.description || "").toLowerCase().includes(q));
    }
    return arr;
  }, [allBooks, ltBooks, language, category, isAdmin, search]);

  const openCategory = async (cat) => {
    if (cat === "listening_test" && !isAdmin) {
      if (getLTToken()) {
        try {
          const { data } = await api.get("/listening-test/books");
          setLtBooks(data);
          setCategory("listening_test");
          return;
        } catch (_) { setLTToken(""); }
      }
      setPwd("");
      setAskPwd(true);
      return;
    }
    setCategory(cat);
  };

  const tryUnlock = async () => {
    if (!pwd) return;
    setUnlocking(true);
    try {
      const { data } = await api.post("/listening-test/unlock", { password: pwd });
      setLTToken(data.token);
      const { data: lt } = await api.get("/listening-test/books");
      setLtBooks(lt);
      setAskPwd(false);
      setCategory("listening_test");
      toast.success("Pasta liberada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUnlocking(false); }
  };

  const canDrag = isAdmin;
  const onDragStart = (idx) => (e) => {
    if (!canDrag) return;
    dragIdx.current = idx; setDragging(true);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    if (!canDrag || dragIdx.current === null) return;
    e.preventDefault();
    if (dropIdx !== idx) setDropIdx(idx);
  };
  const onDragEnd = () => { dragIdx.current = null; setDropIdx(null); setDragging(false); };
  const onDrop = (idx) => (e) => {
    if (!canDrag) return;
    e.preventDefault();
    const from = dragIdx.current;
    dragIdx.current = null; setDropIdx(null); setDragging(false);
    if (from === null || from === idx) return;
    const newVisible = [...visibleBooks];
    const [moved] = newVisible.splice(from, 1);
    newVisible.splice(idx, 0, moved);
    // reconstroi allBooks preservando livros fora desta seção
    const newAll = [];
    let vi = 0;
    for (const b of allBooks) {
      if (b.language === language && b.category === category) {
        newAll.push(newVisible[vi]); vi += 1;
      } else newAll.push(b);
    }
    setAllBooks(newAll);
    setTimeout(() => {
      api.post(`/books/reorder`, { ids: newAll.map((b) => b.id) }).catch((e) => toast.error(formatApiError(e)));
    }, 0);
  };

  const totalsByLang = (lang) => counts[lang].audios + counts[lang].videos + counts[lang].listening_test;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        {/* Avisos ativos */}
        {!isAdmin && (
          <div className="space-y-2 mb-6" data-testid="announcements-list">
            {announcements.filter((a) => !dismissedAnns.has(a.id)).map((a) => (
              <div key={a.id} data-testid={`announcement-${a.id}`} className="flex items-start gap-3 border-l-4 border-[#E30613] bg-red-50 p-4 rounded-sm">
                <Megaphone className="w-5 h-5 text-[#E30613] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-900 whitespace-pre-wrap break-words">{a.text}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#E30613] mt-1 font-semibold">
                    {a.target === "sacoma" ? "Unid. Sacomã" : a.target === "pauliceia" ? "Unid. Paulicéia" : "Todas as unidades"}
                  </p>
                </div>
                <button
                  onClick={() => dismissAnn(a.id)}
                  data-testid={`dismiss-announcement-${a.id}`}
                  className="p-1 text-neutral-500 hover:text-neutral-900 rounded-sm hover:bg-white/60"
                  aria-label="Dispensar aviso"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tela 1: escolha do idioma */}
        {!language && (
          <>
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-3">Olá, {user?.name?.split(" ")[0]}</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-tight">Bem-vindo(a)</h1>
              <p className="text-neutral-500 mt-3 max-w-2xl">Escolha o idioma do seu curso para começar.</p>
            </div>
            {loading ? (
              <div className="text-neutral-500">Carregando...</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="languages-grid">
                {["english", "espanol"].map((lang) => (
                  <LanguageCard
                    key={lang}
                    lang={lang}
                    total={totalsByLang(lang)}
                    onClick={() => { setLanguage(lang); setCategory(null); setSearch(""); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Tela 2: pastas dentro do idioma */}
        {language && !category && (
          <>
            <button
              onClick={() => setLanguage(null)}
              data-testid="back-to-languages"
              className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#E30613] mb-4"
            >
              <ChevronLeft className="w-4 h-4" /> Idiomas
            </button>
            <div className="mb-10">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-3 flex items-center gap-2">
                <Languages className="w-3 h-3" />
                {LANGUAGE_META[language].title}
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-tight">Escolha uma pasta</h1>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="folders-grid">
              {["audios", "videos", "listening_test"].map((cat) => {
                const { Icon, accent } = FOLDER_META[cat];
                return (
                  <FolderCard
                    key={cat}
                    testId={`folder-${cat}`}
                    title={labels[language][cat]}
                    icon={<Icon className="w-7 h-7" />}
                    count={counts[language][cat]}
                    accent={accent}
                    locked={cat === "listening_test" && !isAdmin && !getLTToken()}
                    onClick={() => openCategory(cat)}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* Tela 3: livros */}
        {language && category && (
          <>
            <button
              onClick={() => { setCategory(null); setSearch(""); }}
              data-testid="back-to-folders"
              className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#E30613] mb-4"
            >
              <ChevronLeft className="w-4 h-4" /> Pastas
            </button>
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-3 flex items-center gap-2">
                <Languages className="w-3 h-3" /> {LANGUAGE_META[language].title}
                <span className="text-neutral-300">/</span>
                {(() => { const I = FOLDER_META[category].Icon; return <I className="w-3 h-3" />; })()}
                {labels[language][category]}
              </p>
              <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 tracking-tight">Seus cursos</h1>
              <p className="text-neutral-500 mt-3 max-w-2xl">
                {canDrag
                  ? "Arraste os cursos para reorganizar a ordem em que os alunos os verão."
                  : "Escolha um curso para acessar o conteúdo."}
              </p>
            </div>

            <div className="mb-6 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar curso..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-neutral-200 rounded-sm focus:outline-none focus:border-[#E30613] transition-colors"
                  data-testid="books-search"
                />
              </div>
              {search && <p className="text-xs text-neutral-500 mt-2">{visibleBooks.length} resultado(s)</p>}
            </div>

            {visibleBooks.length === 0 ? (
              <div data-testid="empty-books" className="border border-dashed border-neutral-300 rounded-sm p-12 text-center">
                <BookOpen className="w-10 h-10 text-neutral-400 mx-auto mb-3" />
                <p className="text-neutral-700 font-medium">Nenhum curso nesta pasta ainda.</p>
                <p className="text-sm text-neutral-500 mt-1">
                  {isAdmin ? "Crie um novo curso no Painel Admin." : "Volte em breve ou contate sua escola."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="books-grid">
                {visibleBooks.map((b, idx) => {
                  const isDropTarget = dropIdx === idx && dragging;
                  const p = progressMap[b.id];
                  return (
                    <div
                      key={b.id}
                      draggable={canDrag}
                      onDragStart={canDrag ? onDragStart(idx) : undefined}
                      onDragOver={canDrag ? onDragOver(idx) : undefined}
                      onDragEnd={canDrag ? onDragEnd : undefined}
                      onDrop={canDrag ? onDrop(idx) : undefined}
                      className={`relative ${isDropTarget ? "ring-2 ring-[#E30613] ring-offset-2 rounded-sm" : ""}`}
                      data-testid={`book-card-wrap-${b.id}`}
                    >
                      {canDrag && (
                        <div className="absolute top-2 right-2 z-10 bg-black/80 text-white p-1.5 rounded-sm cursor-grab active:cursor-grabbing">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}
                      <Link
                        to={`/books/${b.id}`}
                        data-testid={`book-card-${b.id}`}
                        className="group block bg-white border border-neutral-200 rounded-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-200 overflow-hidden"
                        draggable={false}
                      >
                        <div
                          className="h-44 bg-black relative overflow-hidden"
                          style={b.cover_url ? { backgroundImage: `url(${b.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                        >
                          {!b.cover_url && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-[#E30613] font-black text-4xl tracking-tighter">FISK</div>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                          <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                            <span className="inline-flex items-center gap-1.5 text-xs text-white bg-[#E30613] px-2 py-1 rounded-sm font-semibold">
                              <Headphones className="w-3 h-3" />
                              {b.lessons_count} {b.lessons_count === 1 ? "arquivo" : "arquivos"}
                            </span>
                            {p && p.percent === 100 && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-white bg-emerald-600 px-2 py-1 rounded-sm font-bold" data-testid={`book-completed-${b.id}`}>
                                ✓ CONCLUÍDO
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="p-5">
                          <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-[#E30613] transition-colors">
                            {b.title}
                          </h3>
                          {b.description && (
                            <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{b.description}</p>
                          )}
                          {!isAdmin && p && p.total > 0 && (
                            <div className="mt-3" data-testid={`book-progress-${b.id}`}>
                              <div className="flex items-center justify-between text-xs mb-1.5">
                                <span className="text-neutral-500 truncate flex items-center gap-1">
                                  {p.last_media_title && p.percent < 100 && (
                                    <>
                                      <PlayCircle className="w-3 h-3 text-[#E30613]" />
                                      <span className="truncate">Continuar: {p.last_media_title}</span>
                                    </>
                                  )}
                                </span>
                                <span className="font-semibold text-neutral-900 shrink-0">{p.percent}%</span>
                              </div>
                              <div className="h-1.5 bg-neutral-200 rounded-sm overflow-hidden">
                                <div className={`h-full transition-all duration-500 ${p.percent === 100 ? "bg-emerald-600" : "bg-[#E30613]"}`} style={{ width: `${p.percent}%` }} />
                              </div>
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <Dialog open={askPwd} onOpenChange={(o) => { if (!o) { setAskPwd(false); setPwd(""); } }}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-600" /> {language ? labels[language].listening_test : "Listening Test"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); tryUnlock(); }} className="space-y-4 pt-2">
              <p className="text-sm text-neutral-600">
                Esta pasta está protegida. Peça ao professor para informar a senha.
              </p>
              <div className="space-y-2">
                <Label>Senha</Label>
                <Input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} className="rounded-sm" data-testid="lt-password-input" required />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={unlocking || !pwd} className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="lt-unlock-submit">
                  {unlocking ? "Validando..." : "Liberar acesso"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Cadastro obrigatório de unidade (para contas antigas sem unit) */}
        <Dialog open={needsUnit} onOpenChange={() => {}}>
          <DialogContent className="rounded-sm" data-testid="unit-required-dialog" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#E30613]" /> Escolha sua unidade
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!unitPick) { toast.error("Selecione uma unidade"); return; }
                setSavingUnit(true);
                const res = await setUnit(unitPick);
                setSavingUnit(false);
                if (res.ok) toast.success("Unidade atualizada");
                else toast.error(res.error || "Erro ao salvar");
              }}
              className="space-y-4 pt-2"
            >
              <p className="text-sm text-neutral-600">
                Antes de continuar, informe em qual unidade FISK você estuda. Isso ajuda seu professor a te encontrar.
              </p>
              <div className="space-y-2">
                <Label>Unidade FISK</Label>
                <Select value={unitPick} onValueChange={setUnitPick}>
                  <SelectTrigger data-testid="unit-required-select" className="rounded-sm">
                    <SelectValue placeholder="Selecione sua unidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sacoma" data-testid="unit-required-sacoma">FISK Sacomã (SP)</SelectItem>
                    <SelectItem value="pauliceia" data-testid="unit-required-pauliceia">FISK Paulicéia (SBC)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingUnit || !unitPick} className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="unit-required-submit">
                  {savingUnit ? "Salvando..." : "Salvar unidade"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
}

function LanguageCard({ lang, total, onClick }) {
  const { title, subtitle, accent } = LANGUAGE_META[lang];
  return (
    <button
      onClick={onClick}
      data-testid={`language-${lang}`}
      className="group relative border border-neutral-200 rounded-sm bg-white hover:-translate-y-1 hover:shadow-xl transition-all duration-200 overflow-hidden text-left"
    >
      <div className="h-52 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 group-hover:opacity-40 transition-opacity"
             style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #E30613 0%, transparent 55%)" }} />
        <div className="absolute top-5 left-5">
          <div className={`${accent} text-white rounded-sm p-3 inline-flex`}>
            <Languages className="w-8 h-8" />
          </div>
        </div>
        <div className="absolute bottom-5 right-5 text-neutral-500 group-hover:text-white transition-colors">
          <ChevronRight className="w-8 h-8" />
        </div>
        <div className="absolute bottom-5 left-5 text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-1">Idioma</p>
          <h3 className="text-3xl font-bold tracking-tight">{title}</h3>
        </div>
      </div>
      <div className="p-5">
        <p className="text-sm text-neutral-600">{subtitle}</p>
        <p className="text-xs text-neutral-500 mt-1">{total} {total === 1 ? "curso" : "cursos"}</p>
      </div>
    </button>
  );
}

function FolderCard({ testId, title, icon, count, accent, onClick, locked }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group relative border border-neutral-200 rounded-sm bg-white hover:-translate-y-1 hover:shadow-xl transition-all duration-200 overflow-hidden text-left"
    >
      <div className="h-44 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity"
             style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #E30613 0%, transparent 50%)" }} />
        <div className="absolute top-4 left-4">
          <div className={`${accent} text-white rounded-sm p-2.5 inline-flex`}>{icon}</div>
        </div>
        {locked && (
          <div className="absolute top-4 right-4">
            <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 bg-amber-900/60 px-2 py-1 rounded-sm font-semibold inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Protegida
            </span>
          </div>
        )}
        <div className="absolute bottom-4 right-4 text-neutral-600 group-hover:text-white transition-colors">
          <ChevronRight className="w-6 h-6" />
        </div>
      </div>
      <div className="p-5 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-neutral-900 group-hover:text-[#E30613] transition-colors">{title}</h3>
          <p className="text-sm text-neutral-500 mt-1">
            {locked ? "Senha necessária" : `${count} ${count === 1 ? "curso" : "cursos"}`}
          </p>
        </div>
      </div>
    </button>
  );
}
